import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanButton, StanInput } from '@/components';
import { useUser } from '@/queries/useUser';
import { useModal } from '@/hooks/useModal';
import { stanNotify } from '@/lib/stanNotify';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { type AnalyticsFormData, analyticsSchema } from '@/features/settings/validations/externalAnalytics';
import { useExternalAnalytics } from '../../queries/useExternalAnalytics';
import { ChangePlanModal } from '@/features/subscriptions/components/ChangePlanModal';

export const ExternalAnalyticsSection: React.FC = () => {
    const { user } = useUser();
    const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});
    const changePlanModal = useModal();
    const { mutateAsync: updateAnalyticsMutation, isPending: isSubmitting } = useExternalAnalytics();

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
        watch,
        setValue,
    } = useForm<AnalyticsFormData>({
        resolver: zodResolver(analyticsSchema),
        defaultValues: {
            analytics_fb_pixel_token: '',
            analytics_google_token: '',
            analytics_tiktok_pixel_token: '',
            pinterest_tag_id: '',
        },
    });

    const watchedValues = watch();

    // Check if user has advanced analytics module
    const isAdvancedAnalyticsAllowed = user?.subscription_tier_allowed_modules?.includes('advanced-analytics') || false;

    // Initialize form data from user
    useEffect(() => {
        if (user?.data) {
            reset({
                analytics_fb_pixel_token: user.data.analytics?.facebook?.token || '',
                analytics_google_token: user.data.analytics?.google?.token || '',
                analytics_tiktok_pixel_token: user.data.analytics?.tiktok?.token || '',
                pinterest_tag_id: user.data.pinterest?.tag_id || '',
            });
        }
    }, [user?.data, reset]);

    const isButtonDisabled = isSubmitting || !isDirty || !isAdvancedAnalyticsAllowed;

    const onSubmit = async (data: AnalyticsFormData) => {
        setFormErrors({});

        try {
            await updateAnalyticsMutation(data);

            // Analytics
            stanAnalytics('analytics-update', {
                meta: {
                    user_id: user?.user_id,
                    username: user?.username,
                },
                props: {
                    update_type: 'external_analytics',
                    platforms: Object.keys(data).filter(key => data[key as keyof AnalyticsFormData]),
                },
            });

            stanNotify({
                type: 'success',
                title: 'Analytics Updated!',
                text: 'Your analytics settings have been saved.',
            });
        } catch (error: any) {
            setFormErrors(error.response?.data?.errors || {});
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: error?.response?.data?.message || 'Please try again later.',
            });
            console.error('Analytics update error:', error);
        }
    };

    const cleanPinterestText = (value: string) => {
        return value.replaceAll('<meta name="p:domain_verify" content="', '').replaceAll('"/>', '');
    };

    return (
        <section className='w-full max-w-3xl space-y-6'>
            <h4>Analytics</h4>

            <div className='relative'>
                {/* Blur/Frost overlay when not allowed */}
                {!isAdvancedAnalyticsAllowed && (
                    <div className='absolute inset-0 z-10 flex flex-col items-center justify-center backdrop-blur-xs'>
                        <h4 className='pb-4'>Want to include your Facebook/Google Pixel?</h4>
                        <StanButton
                            name='upgrade-analytics-button'
                            variant='outline'
                            size='sm'
                            onClick={changePlanModal.open}
                        >
                            <span className='flex items-center gap-2'>
                                <svg
                                    width='20'
                                    height='20'
                                    viewBox='0 0 20 20'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='size-5'
                                >
                                    <path
                                        d='M14.167 8.33333V6.66667C14.167 4.36548 12.3015 2.5 10.0003 2.5C7.69914 2.5 5.83366 4.36548 5.83366 6.66667V8.33333M10.0003 12.0833V13.75M7.33366 17.5H12.667C13.9011 17.5 14.5182 17.5 14.9925 17.2275C15.4085 16.9878 15.7382 16.6581 15.9778 16.2421C16.2503 15.7678 16.2503 15.1507 16.2503 13.9167V12.0833C16.2503 10.8493 16.2503 10.2322 15.9778 9.75792C15.7382 9.34193 15.4085 9.01223 14.9925 8.77248C14.5182 8.5 13.9011 8.5 12.667 8.5H7.33366C6.09964 8.5 5.48263 8.5 5.00834 8.77248C4.59235 9.01223 4.26265 9.34193 4.0229 9.75792C3.75033 10.2322 3.75033 10.8493 3.75033 12.0833V13.9167C3.75033 15.1507 3.75033 15.7678 4.0229 16.2421C4.26265 16.6581 4.59235 16.9878 5.00834 17.2275C5.48263 17.5 6.09964 17.5 7.33366 17.5Z'
                                        stroke='currentColor'
                                        strokeWidth='1.66667'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                </svg>
                                Upgrade Now
                            </span>
                        </StanButton>
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
                    <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                        <div>
                            <StanInput
                                {...register('analytics_fb_pixel_token')}
                                name='analytics_fb_pixel_token'
                                value={watchedValues.analytics_fb_pixel_token || ''}
                                topLabel='Facebook Pixel Id'
                                error={!!errors.analytics_fb_pixel_token || !!formErrors.analytics_fb_pixel_token}
                                hintText={
                                    errors.analytics_fb_pixel_token?.message || formErrors.analytics_fb_pixel_token?.[0]
                                }
                            />
                        </div>

                        <div>
                            <StanInput
                                {...register('analytics_google_token')}
                                name='analytics_google_token'
                                value={watchedValues.analytics_google_token || ''}
                                topLabel='Google Analytics Id'
                                error={!!errors.analytics_google_token || !!formErrors.analytics_google_token}
                                hintText={
                                    errors.analytics_google_token?.message || formErrors.analytics_google_token?.[0]
                                }
                            />
                        </div>
                    </div>

                    <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                        <div>
                            <StanInput
                                {...register('analytics_tiktok_pixel_token')}
                                name='analytics_tiktok_pixel_token'
                                value={watchedValues.analytics_tiktok_pixel_token || ''}
                                topLabel='Tiktok Pixel Id'
                                error={
                                    !!errors.analytics_tiktok_pixel_token || !!formErrors.analytics_tiktok_pixel_token
                                }
                                hintText={
                                    errors.analytics_tiktok_pixel_token?.message ||
                                    formErrors.analytics_tiktok_pixel_token?.[0]
                                }
                            />
                        </div>

                        <div>
                            <StanInput
                                {...register('pinterest_tag_id')}
                                name='pinterest_tag_id'
                                value={watchedValues.pinterest_tag_id || ''}
                                topLabel='Pinterest Claim Tag Id'
                                onBlur={e => {
                                    // Clean the Pinterest meta tag on blur (matches Vue behavior)
                                    const cleanedValue = cleanPinterestText(e.target.value);
                                    if (cleanedValue !== e.target.value) {
                                        setValue('pinterest_tag_id', cleanedValue, { shouldDirty: true });
                                    }
                                }}
                                error={!!errors.pinterest_tag_id || !!formErrors.pinterest_tag_id}
                                hintText={errors.pinterest_tag_id?.message || formErrors.pinterest_tag_id?.[0]}
                            />
                        </div>
                    </div>

                    <div className='flex items-center gap-4'>
                        <StanButton
                            name='settings-external-analytics-update-button'
                            type='submit'
                            disabled={isButtonDisabled}
                            loading={isSubmitting}
                        >
                            Update
                        </StanButton>

                        {isSubmitting && (
                            <div className='text-caption-1-regular flex items-center'>
                                <div className='mr-2 size-4 animate-spin rounded-full border-b-2' />
                                Updating...
                            </div>
                        )}
                    </div>
                </form>
            </div>

            <ChangePlanModal isOpen={changePlanModal.isOpen} onClose={changePlanModal.close} />
        </section>
    );
};
